/**
 * Navigation target of an in-app bell row (notification or task).
 *
 * A task says what to do, so its target must be the place where it can be done. "Physical
 * committee required" is stored with the plain CDE Management page as its link: landing on a
 * paginated list of every pending CDE is not the action, the action is the committee
 * organization dialog of one deal. The deep link is therefore derived here, at click time,
 * from the row code and its payload — never read back from the stored link, because every row
 * already created carries the plain page URL and must keep working without a data migration.
 */

/** CDE Management page (the only screen carrying the committee organization dialog). */
export const CDE_MANAGEMENT_ROUTE = '/crm/crm-cde-management';

/** Query param naming the deal to focus on the CDE Management page. */
export const CDE_FOCUS_DEAL_PARAM = 'deal';

/** Query param naming the dialog to open once the deal row is focused. */
export const CDE_FOCUS_ACTION_PARAM = 'action';

/** Only supported action so far: open the committee organization dialog. */
export const CDE_ORGANIZE_COMMITTEE_ACTION = 'organize-committee';

/** Notification code of the task asking an admin to organize a physical committee. */
export const CDE_PHYSICAL_REQUIRED_CODE = 'cde_physical_required';

export interface InappNotificationTargetRow {
  code?: string | null;
  link?: string | null;
  payload?: Record<string, unknown> | string | null;
}

/**
 * Deal slug carried by a row payload.
 *
 * The backend decodes `payload` before answering, but leaves it as the raw string when the
 * JSON could not be decoded: both shapes have to resolve, otherwise a single malformed row
 * would silently lose its deep link.
 */
export function inappNotificationDealSlug(
  payload: InappNotificationTargetRow['payload']
): string {
  let source: unknown = payload;

  if (typeof source === 'string') {
    try {
      source = JSON.parse(source);
    } catch {
      return '';
    }
  }

  if (!source || typeof source !== 'object' || Array.isArray(source)) {
    return '';
  }

  const slug = (source as Record<string, unknown>)['slug_opportunity'];
  return typeof slug === 'string' ? slug.trim() : '';
}

/**
 * URL the bell navigates to when the row is clicked ('' when the row has nowhere to go).
 *
 * Deep link only for the physical committee task, and only when the payload names a deal:
 * without a deal there is no row to focus and no dialog to open, so the stored page link
 * stays the honest target.
 */
export function resolveInappNotificationTarget(
  row: InappNotificationTargetRow | null | undefined
): string {
  const storedLink = String(row?.link ?? '').trim();
  if (String(row?.code ?? '').trim() !== CDE_PHYSICAL_REQUIRED_CODE) {
    return storedLink;
  }

  const slug = inappNotificationDealSlug(row?.payload);
  if (slug === '') {
    return storedLink;
  }

  return buildCdeOrganizeCommitteeLink(slug);
}

/**
 * Deep link opening the committee organization dialog of one deal.
 *
 * encodeURIComponent rather than URLSearchParams: the latter encodes a space as '+', which the
 * Angular router hands back as a literal '+' — the deal would then never be found.
 */
export function buildCdeOrganizeCommitteeLink(slug: string): string {
  return (
    `${CDE_MANAGEMENT_ROUTE}` +
    `?${CDE_FOCUS_DEAL_PARAM}=${encodeURIComponent(slug)}` +
    `&${CDE_FOCUS_ACTION_PARAM}=${CDE_ORGANIZE_COMMITTEE_ACTION}`
  );
}
